import styled, { css } from 'styled-components';
import { TypographyMixins, typographyMixins } from './typography';
import { colors, Color } from '@ardoq/design-tokens';
import { ReactNode, Ref, useRef, useState } from 'react';
import { useMergedRefs } from '@ardoq/hooks';
import { isTextTruncated } from '@ardoq/common-helpers';
// oxlint-disable-next-line ardoq-lint/no-extraneous-dependencies
import { Tooltip } from '@ardoq/popovers';
import { ellipsisCSS, multiLineEllipsisCSS } from '@ardoq/style-helpers';

export type TextColor = (typeof textColorTokenList)[number];

type StyledTextProps = {
  $variant?: TypographyMixins;
  $marginReset?: boolean;
  $align?: 'left' | 'center' | 'right';
  $color?: TextColor;
  $truncate?: boolean | number;
  $inline?: boolean;
  $strikethrough?: boolean;
};

const inlineBlockCSS = css`
  display: inline-block;
  max-width: 100%;
`;

const StyledText = styled.div<StyledTextProps>`
  ${({ $color }) =>
    $color &&
    css`
      color: ${colors[$color]};
    `}
  ${({ $variant }) => $variant && typographyMixins[$variant]}
  ${({ $marginReset }) => $marginReset && marginResetMixin}
  ${({ $align }) => $align && `text-align: ${$align};`}
  ${({ $truncate }) =>
    $truncate === true
      ? ellipsisCSS
      : typeof $truncate === 'number' && multiLineEllipsisCSS($truncate)}
  ${({ $truncate, $inline }) => $truncate === true && $inline && inlineBlockCSS}
  ${({ $strikethrough }) => $strikethrough && `text-decoration: line-through;`}
`;

// NOTE: some elements may have base margins set (by, for example, Bootstrap)
const marginResetMixin = `
  margin: 0;
`;

type TruncatedTextProps = TextProps & {
  as: 'div' | 'span';
  ref?: Ref<HTMLElement>;
};

const TruncatedText = ({
  children,
  tooltipText,
  color,
  variant,
  align,
  truncate,
  strikethrough,
  as,
  ref: forwardedRef,
  ...props
}: TruncatedTextProps) => {
  const isOnlyEllipsis =
    truncate === 'only-ellipsis' ||
    (typeof truncate === 'object' && truncate.onlyEllipsis === true);
  const lines = typeof truncate === 'object' ? truncate.lines : undefined;

  const ref = useRef<HTMLElement>(null);
  const setRefs = useMergedRefs(ref, forwardedRef);
  const [showTooltip, setShowTooltip] = useState(false);

  const handleMouseOver = () => {
    if (typeof children !== 'string' && tooltipText === undefined) {
      return;
    }
    if (ref.current && isTextTruncated(ref.current, lines)) {
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  return (
    <StyledText
      ref={setRefs}
      onMouseOver={handleMouseOver}
      onMouseLeave={handleMouseLeave}
      $color={color}
      $variant={variant}
      $align={align}
      $truncate={lines ?? true}
      $inline={as === 'span'}
      $strikethrough={strikethrough}
      {...props}
      as={as}
    >
      {showTooltip && !isOnlyEllipsis && (
        <Tooltip
          isOpen={showTooltip}
          onClose={() => setShowTooltip(false)}
          anchorRef={ref}
        >
          {tooltipText ?? children}
        </Tooltip>
      )}
      {children}
    </StyledText>
  );
};

type HeaderProps = {
  color?: TextColor;
  align?: 'left' | 'center' | 'right';
  title?: string;
  children: ReactNode;
  id?: string;
};

export const Header1 = ({ color, align, ...rest }: HeaderProps) => {
  return (
    <StyledText
      as="h1"
      $variant="header1"
      $marginReset
      $align={align}
      $color={color}
      {...rest}
    />
  );
};

export const Header2 = ({ color, align, ...rest }: HeaderProps) => {
  return (
    <StyledText
      as="h2"
      $variant="header2"
      $marginReset
      $align={align}
      $color={color}
      {...rest}
    />
  );
};

export const Header3 = ({ color, align, ...rest }: HeaderProps) => {
  return (
    <StyledText
      as="h3"
      $variant="header3"
      $marginReset
      $align={align}
      $color={color}
      {...rest}
    />
  );
};

export const Header4 = ({ color, align, ...rest }: HeaderProps) => {
  return (
    <StyledText
      as="h4"
      $variant="header4"
      $marginReset
      $align={align}
      $color={color}
      {...rest}
    />
  );
};

export const Header5 = ({ color, align, ...rest }: HeaderProps) => {
  return (
    <StyledText
      as="h5"
      $variant="header5"
      $marginReset
      $align={align}
      $color={color}
      {...rest}
    />
  );
};

type AllowedHTMLAttributes = Pick<React.HTMLAttributes<HTMLElement>, 'id'>;

export type TextProps = {
  variant?:
    | 'text1'
    | 'text1Italic'
    | 'text1Bold'
    | 'text2'
    | 'text2Bold'
    | 'caption'
    | 'captionBold';
  color?: TextColor;
  align?: 'left' | 'center' | 'right';
  children: ReactNode;
  /**
   * Text to display in a tooltip when the text is truncated. Should only be
   * used when children is not a plain string.
   */
  tooltipText?: string;
  truncate?:
    | boolean
    | 'only-ellipsis'
    | { lines: number; onlyEllipsis?: boolean };
  strikethrough?: boolean;
} & AllowedHTMLAttributes;

type TextWithRefProps = TextProps & { ref?: Ref<HTMLDivElement> };

type InlineTextWithRefProps = TextProps & { ref?: Ref<HTMLSpanElement> };

export const textColorTokenList = [
  'textDefault',
  'textModerate',
  'textSubtle',
  'textDisabled',
  'textOnInverted',
  'textOnInvertedSubtle',
  'textOnInvertedStrong',
  'textOnInvertedDisabled',
  'textError',
  'textSuccess',
  'textInfo',
  'textAction',
  'textWarning',
  'textBrand',
] as const satisfies Color[];

export const InlineText = ({
  truncate,
  tooltipText,
  ref,
  ...props
}: InlineTextWithRefProps) => {
  if (truncate) {
    return (
      <TruncatedText
        tooltipText={tooltipText}
        truncate={truncate}
        ref={ref}
        {...props}
        as="span"
      />
    );
  }

  const { color, variant, align, strikethrough, ...rest } = props;

  return (
    <StyledText
      ref={ref}
      $color={color}
      $variant={variant}
      $align={align}
      $strikethrough={strikethrough}
      {...rest}
      as="span"
    />
  );
};

export const Paragraph = ({
  color,
  variant,
  align,
  strikethrough,
  ...rest
}: TextProps) => {
  return (
    <StyledText
      $color={color}
      $variant={variant}
      $align={align}
      $strikethrough={strikethrough}
      $marginReset
      {...rest}
      as="p"
    />
  );
};

export const Text = ({
  truncate,
  tooltipText,
  ref,
  ...props
}: TextWithRefProps) => {
  if (truncate) {
    return (
      <TruncatedText
        tooltipText={tooltipText}
        truncate={truncate}
        ref={ref}
        {...props}
        as="div"
      />
    );
  }

  const { color, variant, align, strikethrough, ...rest } = props;

  return (
    <StyledText
      ref={ref}
      $color={color}
      $variant={variant}
      $align={align}
      $strikethrough={strikethrough}
      {...rest}
      as="div"
    />
  );
};
